import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import { API_ESA_OFFICE } from "utils";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha } from "@mui/material/styles";

// Material Dashboard 2 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

function DaftarUsers() {
  const [users, setUsers] = useState([]);
  const [dinasId, setDinasId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [listDinas, setListDinas] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedDinas, setSelectedDinas] = useState(null);
  const [dinasStruktur, setDinasStruktur] = useState(null);
  const [loadingDinas, setLoadingDinas] = useState(true);
  const [loadingStruktur, setLoadingStruktur] = useState(true);
  const [jabatanOptions, setJabatanOptions] = useState([]);
  const [bidangOptions, setBidangOptions] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isValidFile, setIsValidFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileImgError, setProfileImgError] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  /** Filter daftar kartu: semua | status_EO aktif | selain aktif */
  const [eoFilter, setEoFilter] = useState("all");
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nama: "",
    email: "",
    nama_jabatan: "",
    pangkat: "",
    facebook: "",
    instagram: "",
    profil: null,
    instansi: "",
    jabatan: "",
    bidang: "",
    status: "",
    unit: "",
    bio: "",
    noHP: "",
    status_EO: "Aktif",
    // Field internal untuk keperluan backend (tidak ditampilkan)
    dinas_id: "",
    bidang_id: "",
    level_jabatan: "",
  });
  const navigate = useNavigate();

  // Fungsi untuk fetching struktur dinas
  const fetchStruktur = async (dinasId) => {
    if (!dinasId) return;

    setLoadingStruktur(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}dinas/${dinasId}/struktur`);
      setDinasStruktur(res.data && res.data[0] ? res.data[0] : null);

      // Ambil opsi jabatan dari dinasStruktur jika ada
      const jabatanOptions =
        res.data[0] && Array.isArray(res.data[0].struktur)
          ? res.data[0].struktur.map((j) => ({
              value: j.nama_jabatan,
              label: j.nama_jabatan,
            }))
          : [];
      setJabatanOptions(jabatanOptions);
      // Ambil opsi bidang dari dinasStruktur jika ada
      const bidangOptions =
        res.data[0] && Array.isArray(res.data[0].bidang)
          ? res.data[0].bidang.map((b) => ({
              value: b.bidang_id,
              label: b.bidang,
            }))
          : [];
      setBidangOptions(bidangOptions);
    } catch (err) {
      setDinasStruktur(null);
    }
    setLoadingStruktur(false);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserStatus(user.status);
      setCurrentUserId(user.user_id ?? user.uid_RSS ?? null);
      fetchListDinas(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (dinasId) {
      fetchUsers();
    }
  }, [dinasId]);

  // Effect untuk menangani perubahan selectedDinas (hanya untuk reset form data)
  useEffect(() => {
    if (selectedDinas && selectedDinas.dinas_id) {
      fetchStruktur(selectedDinas.dinas_id);
      setFormData((prevData) => ({
        ...prevData,
        instansi: selectedDinas.nama_dinas,
        dinas_id: selectedDinas.dinas_id,
        // Reset jabatan dan bidang ketika dinas berubah
        jabatan: "",
        bidang: "",
        bidang_id: "",
        level_jabatan: "",
      }));
    }
  }, [selectedDinas]);

  // Fungsi untuk mengambil list dinas
  const fetchListDinas = async (dinasId) => {
    setLoadingDinas(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}dinas`);
      const dinasData = res.data || [];
      const sepesifikDinas = dinasData.filter((item) => {
        return item.dinas_id == dinasId;
      });
      setListDinas(sepesifikDinas);
    } catch (err) {
      console.error("Error fetching list dinas:", err);
      setListDinas([]);
    }
    setLoadingDinas(false);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/dinas-all/${dinasId}`
      );
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function untuk mendapatkan label pangkat
  const getPangkatLabel = (value) => {
    const pangkatMap = {
      IA: "Juru Muda (I/a)",
      IB: "Juru Muda Tingkat I (I/b)",
      IC: "Juru (I/c)",
      ID: "Juru Tingkat I (I/d)",
      IIA: "Pengatur Muda (II/a)",
      IIB: "Pengatur Muda Tingkat I (II/b)",
      IIC: "Pengatur (II/c)",
      IID: "Pengatur Tingkat I (II/d)",
      IIIA: "Penata Muda (III/a)",
      IIIB: "Penata Muda Tingkat I (III/b)",
      IIIC: "Penata (III/c)",
      IIID: "Penata Tingkat I (III/d)",
      IVA: "Pembina (IV/a)",
      IVB: "Pembina Tingkat I (IV/b)",
      IVC: "Pembina Utama Muda (IV/c)",
      IVD: "Pembina Utama Madya (IV/d)",
    };
    return pangkatMap[value] || value;
  };

  const handleOpenDialog = async (user) => {
    if (!user) return;

    setLoadingDialog(true);
    setSelectedUser(user);

    // Reset upload state
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsValidFile(false);
    setUploading(false);
    setProfileImgError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Set selectedDinas berdasarkan dinas_id user
    const userDinas = listDinas.find((d) => d.dinas_id === user.dinas_id);
    if (userDinas) {
      setSelectedDinas(userDinas);
      // Fetch struktur dinas terlebih dahulu sebelum membuka dialog
      await fetchStruktur(userDinas.dinas_id);
    }

    setFormData({
      nama: user.nama || "",
      email: user.email || "",
      nama_jabatan: user.nama_jabatan || "",
      pangkat: user.pangkat || "",
      facebook: user.facebook || "",
      instagram: user.instagram || "",
      profil: user.profil || null,
      instansi: user.nama_dinas || "",
      jabatan: user.nama_jabatan || "",
      bidang: user.nama_bidang || "",
      status: user.status || "",
      unit: user.jenis_unit || "",
      bio: user.bio || "",
      noHP: user.noHP || "",
      status_EO: user.status_EO || "Aktif",
      // Field internal untuk keperluan backend (tidak ditampilkan)
      dinas_id: user.dinas_id || "",
      bidang_id: user.bidang_id || "",
      level_jabatan: user.level_jabatan || "",
    });

    setLoadingDialog(false);
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, profil: file });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];

    // Cleanup preview URL sebelumnya jika ada
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      return;
    }

    // Validasi ukuran file (maksimal 1MB)
    if (file.size > 1 * 1024 * 1024) {
      swal({
        title: "Error",
        text: "Ukuran file melebihi 1 MB. Silakan pilih file yang lebih kecil",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validasi tipe file
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      swal({
        title: "Error",
        text: "Format file tidak didukung. Hanya JPG dan PNG yang diperbolehkan",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // File valid
    setSelectedFile(file);
    setIsValidFile(true);

    // Buat preview URL menggunakan createObjectURL untuk efisiensi
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleUploadAvatar = async () => {
    if (!selectedFile || !selectedUser?.uid_RSS) {
      swal({
        title: "Error",
        text: "Silakan pilih gambar terlebih dahulu",
        icon: "error",
      });
      return;
    }

    setUploading(true);
    try {
      // Buat File object baru dengan nama yang diubah menjadi uid_RSS
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const validExtension = ["jpg", "jpeg", "png"].includes(fileExtension)
        ? fileExtension === "jpeg"
          ? "jpg"
          : fileExtension
        : "jpg"; // Default ke jpg jika extension tidak valid

      // Buat File baru dengan nama uid_RSS.{extension}
      const renamedFile = new File(
        [selectedFile],
        `${selectedUser.uid_RSS}.${validExtension}`,
        {
          type: selectedFile.type,
          lastModified: selectedFile.lastModified,
        }
      );

      const formData = new FormData();
      formData.append("photo", renamedFile);

      const uploadResponse = await axios.post(
        `https://smartdh.rotendaokab.go.id/api/users/upload-avatar`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (uploadResponse.data && uploadResponse.data.success === 1) {
        // Cleanup preview URL sebelum close
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        swal({
          title: "Berhasil!",
          text:
            uploadResponse.data.message || "Gambar profil berhasil diupdate",
          icon: "success",
          button: "OK",
        });

        // Reset state
        setSelectedFile(null);
        setPreviewUrl(null);
        setIsValidFile(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Refresh users list untuk mendapatkan gambar baru
        await fetchUsers();
      } else {
        throw new Error(
          uploadResponse.data?.message || "Gagal mengupload gambar"
        );
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal mengupload gambar profil",
        icon: "error",
        button: "OK",
      });
    } finally {
      setUploading(false);
    }
  };

  // Ambil opsi jabatan dari dinasStruktur jika ada

  const handleEdit = async () => {
    if (!selectedUser) return;

    try {
      // Susun payload JSON tanpa mengirim gambar (profil)
      const {
        nama,
        email,
        nama_jabatan,
        jabatan,
        level_jabatan,
        dinas_id,
        bidang_id,
        bidang,
        pangkat,
        status,
        facebook,
        instagram,
        unit,
        noHP,
        status_EO,
      } = formData;

      // Backend PUT membutuhkan: nama, email, nama_jabatan, level_jabatan, dinas_id, bidang_id, pangkat, status, facebook, instagram, noHP, status_EO
      const payload = {
        nama,
        email,
        nama_jabatan: nama_jabatan || jabatan || "",
        level_jabatan: level_jabatan || "",
        dinas_id: dinas_id || selectedDinas?.dinas_id || null,
        bidang_id: bidang_id || null,
        pangkat: pangkat || null,
        status: status || "user",
        facebook: facebook || null,
        instagram: instagram || null,
        noHP: noHP || null,
        status_EO: status_EO || "Aktif",
      };

      await axios.put(
        `${API_ESA_OFFICE}users/${selectedUser.user_id}`,
        payload
      );
      swal({
        title: "Berhasil!",
        text: "Data user berhasil diperbarui",
        icon: "success",
        button: "OK",
      });
      setOpenDialog(false);
      fetchUsers();
    } catch (error) {
      swal({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Terjadi kesalahan saat memperbarui data",
        icon: "error",
        button: "OK",
      });
    }
  };

  const handleDelete = (userId) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data user akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(`${API_ESA_OFFICE}users/${userId}`);
          swal({
            title: "Berhasil!",
            text: "User berhasil dihapus",
            icon: "success",
            button: "OK",
          });
          fetchUsers();
        } catch (error) {
          swal({
            title: "Error!",
            text:
              error.response?.data?.message ||
              "Terjadi kesalahan saat menghapus user",
            icon: "error",
            button: "OK",
          });
        }
      }
    });
  };

  const getWhatsAppUrl = (phone) => {
    if (!phone || typeof phone !== "string") return null;
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length < 9) return null;
    const num = cleaned.startsWith("0") ? "62" + cleaned.slice(1) : cleaned.startsWith("62") ? cleaned : "62" + cleaned;
    return `https://wa.me/${num}?text=${encodeURIComponent("Esa office Connect")}`;
  };

  /** Aksen minimal elegan (biru / hijau / merah) */
  const acc = { blue: "#1565c0", green: "#2e7d32", red: "#c62828" };
  const chipMinimal = (tone) => {
    const c = acc[tone] || acc.blue;
    return {
      height: 22,
      fontSize: "0.65rem",
      fontWeight: 600,
      bgcolor: alpha(c, 0.08),
      color: c,
      border: `1px solid ${alpha(c, 0.4)}`,
      "& .MuiChip-label": { px: 1 },
    };
  };
  const roleChipTone = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "admin") return "green";
    if (s === "super admin" || s === "pimpinan") return "red";
    return "blue";
  };
  const eoChipTone = (value) => {
    if (value === "Aktif") return "green";
    if (value === "Non Aktif" || value === "Meninggal") return "red";
    return "blue";
  };

  const dlgLabelSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "text.secondary",
    display: "block",
    mb: 0.35,
  };

  const isStatusEoAktif = (u) =>
    String(u?.status_EO ?? "").trim().toLowerCase() === "aktif";

  const pegawaiStats = useMemo(() => {
    let aktif = 0;
    let tidakAktif = 0;
    users.forEach((u) => {
      if (isStatusEoAktif(u)) aktif += 1;
      else tidakAktif += 1;
    });
    return { total: users.length, aktif, tidakAktif };
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (eoFilter === "aktif") return users.filter((u) => isStatusEoAktif(u));
    if (eoFilter === "tidak_aktif") return users.filter((u) => !isStatusEoAktif(u));
    return users;
  }, [users, eoFilter]);

  const rekapChipSx = (borderColor, selected) => ({
    height: 26,
    fontSize: "0.8rem",
    fontWeight: 600,
    border: "none",
    borderLeft: "3px solid",
    borderLeftColor: borderColor,
    bgcolor: alpha(borderColor, selected ? 0.2 : 0.1),
    color: borderColor,
    borderRadius: "6px",
    cursor: "pointer",
    transition: "box-shadow 0.2s ease, background-color 0.2s ease",
    "& .MuiChip-icon": { color: borderColor },
    "& .MuiChip-label": { px: 1.25 },
    ...(selected && {
      boxShadow: `0 0 0 2px ${alpha(borderColor, 0.4)}`,
    }),
    "&:hover": {
      bgcolor: alpha(borderColor, selected ? 0.26 : 0.14),
    },
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">Daftar Users</MDTypography>
            </Grid>
            <Grid item>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => navigate("/team/new-user")}
              >
                <Icon>add</Icon>&nbsp;Tambah User
              </MDButton>
            </Grid>
          </Grid>
          {!loading && (
            <MDBox mt={2.5}>
              <MDTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.35, letterSpacing: "0.02em" }}>
                Rekapan pegawai
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25, fontSize: "0.7rem", opacity: 0.85 }}>
                Klik chip untuk menampilkan daftar sesuai status EO
              </MDTypography>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item>
                  <Chip
                    icon={<Icon sx={{ fontSize: 18 }}>group</Icon>}
                    label={`Total pegawai: ${pegawaiStats.total}`}
                    onClick={() => setEoFilter("all")}
                    sx={rekapChipSx(acc.blue, eoFilter === "all")}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    icon={<Icon sx={{ fontSize: 18 }}>check_circle</Icon>}
                    label={`Aktif: ${pegawaiStats.aktif}`}
                    onClick={() => setEoFilter("aktif")}
                    sx={rekapChipSx(acc.green, eoFilter === "aktif")}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    icon={<Icon sx={{ fontSize: 18 }}>highlight_off</Icon>}
                    label={`Tidak aktif: ${pegawaiStats.tidakAktif}`}
                    onClick={() => setEoFilter("tidak_aktif")}
                    sx={rekapChipSx(acc.red, eoFilter === "tidak_aktif")}
                  />
                </Grid>
              </Grid>
            </MDBox>
          )}
        </MDBox>

        {loading ? (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={6}
          >
            <CircularProgress size={40} />
            <MDTypography variant="body2" color="text" ml={2}>
              Memuat data pengguna...
            </MDTypography>
          </MDBox>
        ) : users.length === 0 ? (
          <MDBox py={6} textAlign="center">
            <MDTypography variant="body1" color="text.secondary">
              Belum ada data pegawai.
            </MDTypography>
          </MDBox>
        ) : filteredUsers.length === 0 ? (
          <MDBox py={6} textAlign="center">
            <MDTypography variant="body1" color="text.secondary" mb={1}>
              Tidak ada pegawai untuk filter status EO ini.
            </MDTypography>
            <MDButton variant="text" color="info" size="small" onClick={() => setEoFilter("all")}>
              Tampilkan semua pegawai
            </MDButton>
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {filteredUsers.map((user, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${user.user_id || user.uid_RSS || "user"}-${index}`}>
                <Card
                  onClick={() => setExpandedUser(user)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: "pointer",
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "divider",
                    borderLeft: "3px solid",
                    borderLeftColor: acc.blue,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${alpha(acc.blue, 0.12)}`,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      "&:last-child": { pb: 2 },
                    }}
                  >
                    <MDBox display="flex" gap={1.75} alignItems="flex-start" mb={1.5}>
                      <MDBox
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          flexShrink: 0,
                          overflow: "hidden",
                          bgcolor: alpha(acc.blue, 0.06),
                          border: `2px solid ${alpha(acc.blue, 0.35)}`,
                        }}
                      >
                        {user.profil ? (
                          <img
                            src={`https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`}
                            alt={user.nama}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const sibling = e.currentTarget.nextSibling;
                              if (sibling) sibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <MDBox
                          display={user.profil ? "none" : "flex"}
                          alignItems="center"
                          justifyContent="center"
                          sx={{ width: "100%", height: "100%", color: alpha(acc.blue, 0.5) }}
                        >
                          <Icon sx={{ fontSize: 28 }}>person</Icon>
                        </MDBox>
                      </MDBox>
                      <MDBox flex={1} minWidth={0}>
                        <MDTypography
                          variant="body2"
                          fontWeight={600}
                          sx={{ lineHeight: 1.35, color: "text.primary", mb: 0.25 }}
                        >
                          {user.nama}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            mb: 0.35,
                          }}
                        >
                          {user.email || "—"}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          display="block"
                          sx={{ color: acc.blue, fontWeight: 600, fontSize: "0.7rem" }}
                        >
                          {user.nama_jabatan || "—"}
                        </MDTypography>
                        {user.nama_bidang ? (
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.25, fontSize: "0.68rem" }}
                          >
                            {user.nama_bidang}
                          </MDTypography>
                        ) : null}
                      </MDBox>
                    </MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={0.75} mb={1.25}>
                      {user.status ? (
                        <Chip
                          label={user.status}
                          size="small"
                          sx={{ ...chipMinimal(roleChipTone(user.status)), textTransform: "capitalize" }}
                        />
                      ) : null}
                      {user.status_EO ? (
                        <Chip
                          label={user.status_EO}
                          size="small"
                          sx={chipMinimal(eoChipTone(user.status_EO))}
                        />
                      ) : null}
                      {user.pangkat ? (
                        <Chip
                          label={user.pangkat}
                          size="small"
                          sx={{ ...chipMinimal("blue"), maxWidth: 100 }}
                        />
                      ) : null}
                    </MDBox>
                    {user.noHP ? (
                      <MDBox
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                        mb={1}
                        sx={{
                          py: 0.5,
                          px: 0.75,
                          borderRadius: 1,
                          bgcolor: alpha(acc.green, 0.05),
                          borderLeft: `3px solid ${alpha(acc.green, 0.55)}`,
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.5} minWidth={0}>
                          <Icon sx={{ fontSize: 14, color: acc.green, opacity: 0.85 }}>phone</Icon>
                          <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            {user.noHP}
                          </MDTypography>
                        </MDBox>
                        {getWhatsAppUrl(user.noHP) && (
                          <MDBox
                            component="a"
                            href={getWhatsAppUrl(user.noHP)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.35,
                              px: 0.85,
                              py: 0.2,
                              borderRadius: 1,
                              border: `1px solid ${alpha(acc.green, 0.45)}`,
                              color: acc.green,
                              bgcolor: alpha(acc.green, 0.06),
                              textDecoration: "none",
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              "&:hover": { bgcolor: alpha(acc.green, 0.12) },
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                            WA
                          </MDBox>
                        )}
                      </MDBox>
                    ) : null}
                    {user.facebook ? (
                      <MDBox
                        component="a"
                        href={`https://facebook.com/${user.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        mb={1}
                        sx={{
                          color: acc.blue,
                          textDecoration: "none",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          "&:hover": { textDecoration: "underline", color: acc.blue },
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }}>facebook</Icon>
                        <MDTypography variant="caption" sx={{ color: "inherit" }}>
                          {user.facebook}
                        </MDTypography>
                      </MDBox>
                    ) : null}
                    {(userStatus === "admin" ||
                      userStatus === "super admin" ||
                      user.user_id === currentUserId ||
                      user.uid_RSS === currentUserId) && (
                      <MDBox
                        display="flex"
                        gap={1}
                        justifyContent="flex-start"
                        flexWrap="wrap"
                        mt="auto"
                        pt={1.25}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          borderTop: "1px solid",
                          borderColor: alpha(acc.blue, 0.12),
                        }}
                      >
                        <MDButton
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(user);
                          }}
                          disabled={loadingDialog}
                          sx={{
                            minWidth: "auto",
                            px: 1.5,
                            textTransform: "none",
                            fontSize: "0.7rem",
                            borderColor: alpha(acc.blue, 0.45),
                            color: acc.blue,
                            "&:hover": {
                              borderColor: acc.blue,
                              bgcolor: alpha(acc.blue, 0.06),
                            },
                          }}
                        >
                          {loadingDialog ? (
                            <CircularProgress size={16} sx={{ color: acc.blue }} />
                          ) : (
                            <>
                              <Icon sx={{ fontSize: 16, mr: 0.35 }}>edit</Icon>
                              Edit
                            </>
                          )}
                        </MDButton>
                        {(userStatus === "admin" ||
                          userStatus === "super admin") && (
                          <MDButton
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(user.user_id);
                            }}
                            sx={{
                              minWidth: "auto",
                              px: 1.5,
                              textTransform: "none",
                              fontSize: "0.7rem",
                              borderColor: alpha(acc.red, 0.45),
                              color: acc.red,
                              "&:hover": {
                                borderColor: acc.red,
                                bgcolor: alpha(acc.red, 0.06),
                              },
                            }}
                          >
                            <Icon sx={{ fontSize: 16, mr: 0.35 }}>delete</Icon>
                            Hapus
                          </MDButton>
                        )}
                      </MDBox>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog
          open={!!expandedUser}
          onClose={() => setExpandedUser(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            onClick: (e) => e.stopPropagation(),
            sx: {
              width: "100%",
              maxWidth: 520,
              minWidth: { xs: "auto", sm: 380 },
              m: 2,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
              borderLeft: `3px solid ${acc.blue}`,
              boxShadow: `0 16px 48px ${alpha("#000", 0.12)}`,
            },
          }}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: alpha("#000", 0.45),
            },
          }}
        >
          {expandedUser && (
            <MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                gap={2}
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderBottom: `1px solid ${alpha(acc.blue, 0.12)}`,
                }}
              >
                <MDBox
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    flexShrink: 0,
                    overflow: "hidden",
                    bgcolor: alpha(acc.blue, 0.06),
                    border: `2px solid ${alpha(acc.blue, 0.35)}`,
                  }}
                >
                  {expandedUser.profil ? (
                    <img
                      src={`https://smartdh.rotendaokab.go.id/uploads/${expandedUser.uid_RSS}.jpg`}
                      alt={expandedUser.nama}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const sibling = e.currentTarget.nextSibling;
                        if (sibling) sibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <MDBox
                    display={expandedUser.profil ? "none" : "flex"}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: "100%", height: "100%", color: alpha(acc.blue, 0.45) }}
                  >
                    <Icon sx={{ fontSize: 36 }}>person</Icon>
                  </MDBox>
                </MDBox>
                <MDBox flex={1} minWidth={0}>
                  <MDTypography variant="body1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                    {expandedUser.nama}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {expandedUser.email || "—"}
                  </MDTypography>
                </MDBox>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedUser(null);
                  }}
                  aria-label="tutup"
                  sx={{
                    flexShrink: 0,
                    border: `1px solid ${alpha(acc.blue, 0.35)}`,
                    color: acc.blue,
                    borderRadius: 1,
                    "&:hover": { bgcolor: alpha(acc.blue, 0.08) },
                  }}
                >
                  <Icon sx={{ fontSize: 20 }}>close</Icon>
                </IconButton>
              </MDBox>

              <MDBox sx={{ p: 2.5, pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDBox
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        bgcolor: alpha(acc.blue, 0.04),
                        borderLeft: `3px solid ${alpha(acc.blue, 0.45)}`,
                      }}
                    >
                      <MDTypography sx={dlgLabelSx}>Email</MDTypography>
                      <MDTypography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {expandedUser.email || "—"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        bgcolor: alpha(acc.blue, 0.04),
                        borderLeft: `3px solid ${alpha(acc.blue, 0.45)}`,
                      }}
                    >
                      <MDTypography sx={dlgLabelSx}>Jabatan</MDTypography>
                      <MDTypography variant="body2" sx={{ color: acc.blue, fontWeight: 600 }}>
                        {expandedUser.nama_jabatan || "—"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox sx={{ p: 1.25, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <MDTypography sx={dlgLabelSx}>Bidang</MDTypography>
                      <MDTypography variant="body2">{expandedUser.nama_bidang || "—"}</MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox sx={{ p: 1.25, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <MDTypography sx={dlgLabelSx}>Pangkat</MDTypography>
                      <MDTypography variant="body2">
                        {expandedUser.pangkat ? getPangkatLabel(expandedUser.pangkat) : "—"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        bgcolor: alpha(acc.green, 0.05),
                        borderLeft: `3px solid ${alpha(acc.green, 0.55)}`,
                      }}
                    >
                      <MDTypography sx={dlgLabelSx}>No. HP</MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <MDTypography variant="body2">{expandedUser.noHP || "—"}</MDTypography>
                        {expandedUser.noHP && getWhatsAppUrl(expandedUser.noHP) && (
                          <MDBox
                            component="a"
                            href={getWhatsAppUrl(expandedUser.noHP)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.35,
                              px: 1,
                              py: 0.35,
                              borderRadius: 1,
                              border: `1px solid ${alpha(acc.green, 0.45)}`,
                              color: acc.green,
                              bgcolor: alpha(acc.green, 0.06),
                              textDecoration: "none",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              "&:hover": { bgcolor: alpha(acc.green, 0.12) },
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                            WhatsApp
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox sx={{ p: 1.25, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <MDTypography sx={dlgLabelSx}>Status</MDTypography>
                      <MDBox display="flex" gap={0.75} flexWrap="wrap">
                        <Chip
                          label={expandedUser.status || "—"}
                          size="small"
                          sx={{
                            ...chipMinimal(roleChipTone(expandedUser.status)),
                            textTransform: "capitalize",
                          }}
                        />
                        <Chip
                          label={expandedUser.status_EO || "Aktif"}
                          size="small"
                          sx={chipMinimal(eoChipTone(expandedUser.status_EO))}
                        />
                      </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox sx={{ p: 1.25, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <MDTypography sx={dlgLabelSx}>Facebook</MDTypography>
                      {expandedUser.facebook ? (
                        <MDBox
                          component="a"
                          href={`https://facebook.com/${expandedUser.facebook}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: acc.blue,
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {expandedUser.facebook}
                        </MDBox>
                      ) : (
                        <MDTypography variant="body2">—</MDTypography>
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDBox
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        bgcolor: alpha(acc.blue, 0.04),
                        borderLeft: `3px solid ${alpha(acc.blue, 0.35)}`,
                      }}
                    >
                      <MDTypography sx={dlgLabelSx}>Instagram</MDTypography>
                      <MDTypography variant="body2" sx={{ color: expandedUser.instagram ? acc.blue : "inherit", fontWeight: expandedUser.instagram ? 500 : 400 }}>
                        {expandedUser.instagram || "—"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>

                {(userStatus === "admin" ||
                  userStatus === "super admin" ||
                  expandedUser.user_id === currentUserId ||
                  expandedUser.uid_RSS === currentUserId) && (
                  <MDBox
                    mt={2.5}
                    pt={2}
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    sx={{ borderTop: `1px solid ${alpha(acc.blue, 0.12)}` }}
                  >
                    <MDButton
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedUser(null);
                        handleOpenDialog(expandedUser);
                      }}
                      disabled={loadingDialog}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        borderColor: alpha(acc.blue, 0.45),
                        color: acc.blue,
                        "&:hover": {
                          borderColor: acc.blue,
                          bgcolor: alpha(acc.blue, 0.06),
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 18, mr: 0.5 }}>edit</Icon>
                      Edit
                    </MDButton>
                    {(userStatus === "admin" || userStatus === "super admin") && (
                      <MDButton
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedUser(null);
                          handleDelete(expandedUser.user_id);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          borderColor: alpha(acc.red, 0.45),
                          color: acc.red,
                          "&:hover": {
                            borderColor: acc.red,
                            bgcolor: alpha(acc.red, 0.06),
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 18, mr: 0.5 }}>delete</Icon>
                        Hapus
                      </MDButton>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          )}
        </Dialog>

        <Dialog
          open={openDialog}
          onClose={() => {
            // Cleanup preview URL saat dialog ditutup
            if (previewUrl) {
              URL.revokeObjectURL(previewUrl);
            }
            setOpenDialog(false);
            setSelectedFile(null);
            setPreviewUrl(null);
            setIsValidFile(false);
            setProfileImgError(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <MDTypography variant="h4" align="center" fontWeight="bold">
              Edit User - {selectedUser?.nama || ""}
            </MDTypography>
          </DialogTitle>
          <DialogContent>
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb={4}
            >
              <MDBox position="relative" display="inline-block">
                {previewUrl ? (
                  <MDBox
                    component="img"
                    src={previewUrl}
                    alt="Profile"
                    sx={{
                      width: 200,
                      height: 200,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "4px solid #fff",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    }}
                  />
                ) : selectedUser?.profil && selectedUser?.uid_RSS && !profileImgError ? (
                  <MDBox
                    component="img"
                    src={`https://smartdh.rotendaokab.go.id/uploads/${selectedUser.uid_RSS}.jpg`}
                    alt="Profile"
                    sx={{
                      width: 200,
                      height: 200,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "4px solid #fff",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    }}
                    onError={() => setProfileImgError(true)}
                  />
                ) : null}
                {(!previewUrl && (!selectedUser?.profil || !selectedUser?.uid_RSS || profileImgError)) && (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 200,
                      height: 200,
                      borderRadius: "50%",
                      border: "4px solid #fff",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                      bgcolor: "grey.200",
                      color: "grey.500",
                    }}
                  >
                    <Icon sx={{ fontSize: 80 }}>person</Icon>
                  </MDBox>
                )}
                {userStatus === "super admin" && (
                  <MDBox
                    component="label"
                    htmlFor="profil-upload-avatar"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      cursor: "pointer",
                      backgroundColor: "info.main",
                      color: "white",
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        backgroundColor: "info.dark",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }}>edit</Icon>
                  </MDBox>
                )}
              </MDBox>
              {userStatus === "super admin" && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="profil-upload-avatar"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={1}
                    textAlign="center"
                  >
                    Format: JPG, PNG | Maksimal: 1 MB
                  </MDTypography>
                  {selectedFile && (
                    <MDBox mt={1} textAlign="center">
                      <MDTypography variant="caption" color="text.secondary">
                        File: {selectedFile.name} (
                        {(selectedFile.size / 1024).toFixed(2)} KB)
                      </MDTypography>
                    </MDBox>
                  )}
                  {isValidFile && (
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={handleUploadAvatar}
                      disabled={uploading}
                      sx={{ mt: 2 }}
                      startIcon={
                        uploading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {uploading ? "Mengupload..." : "Upload Foto"}
                    </MDButton>
                  )}
                </>
              )}
            </MDBox>

            {/* Akun RSS Section */}
            <MDBox mb={3}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Akun Rote Smart Service
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Informasi akun RSS user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  type="email"
                  label="Email Address"
                  fullWidth
                  value={formData.email}
                />
              </Grid>
            </Grid>

            {/* Info User Section */}
            <MDBox mb={3} mt={4}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Informasi User
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Data pribadi dan jabatan user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <MDInput
                  type="text"
                  label="Nama Lengkap"
                  fullWidth
                  value={formData.nama}
                  onChange={(e) =>
                    setFormData({ ...formData, nama: e.target.value })
                  }
                  placeholder="Nama lengkap beserta gelar"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Instansi
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={selectedDinas}
                  onChange={(event, newValue) => {
                    setSelectedDinas(newValue);
                  }}
                  options={listDinas}
                  getOptionLabel={(option) => option.nama_dinas || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.dinas_id === value?.dinas_id
                  }
                  loading={loadingDinas}
                  disabled={loadingDinas}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Dinas"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.dinas_id}>
                      {option.nama_dinas}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Jabatan
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.jabatan
                      ? {
                          value: formData.jabatan,
                          label: formData.jabatan,
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    const selectedJabatan = newValue?.value || "";
                    let level = "";
                    if (
                      dinasStruktur &&
                      Array.isArray(dinasStruktur.struktur)
                    ) {
                      const found = dinasStruktur.struktur.find(
                        (j) => j.nama_jabatan === selectedJabatan
                      );
                      if (found) level = found.level_jabatan;
                    }
                    setFormData({
                      ...formData,
                      nama_jabatan: selectedJabatan,
                      jabatan: selectedJabatan,
                      level_jabatan: level,
                    });
                  }}
                  options={jabatanOptions}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  loading={loadingStruktur}
                  disabled={loadingStruktur}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Jabatan"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Bidang
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.bidang
                      ? {
                          value: formData.bidang_id,
                          label: formData.bidang,
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    const selectedBidangId = newValue?.value || "";
                    const selectedBidang =
                      dinasStruktur && Array.isArray(dinasStruktur.bidang)
                        ? dinasStruktur.bidang.find(
                            (b) => b.bidang_id == selectedBidangId
                          )
                        : null;
                    setFormData({
                      ...formData,
                      bidang_id: selectedBidangId,
                      bidang: selectedBidang ? selectedBidang.bidang : "",
                    });
                  }}
                  options={bidangOptions}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  loading={loadingStruktur}
                  disabled={loadingStruktur}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Bidang"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Pangkat
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.pangkat
                      ? {
                          value: formData.pangkat,
                          label: getPangkatLabel(formData.pangkat),
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      pangkat: newValue?.value || "",
                    });
                  }}
                  options={[
                    { value: "IA", label: "Juru Muda (I/a)" },
                    { value: "IB", label: "Juru Muda Tingkat I (I/b)" },
                    { value: "IC", label: "Juru (I/c)" },
                    { value: "ID", label: "Juru Tingkat I (I/d)" },
                    { value: "IIA", label: "Pengatur Muda (II/a)" },
                    { value: "IIB", label: "Pengatur Muda Tingkat I (II/b)" },
                    { value: "IIC", label: "Pengatur (II/c)" },
                    { value: "IID", label: "Pengatur Tingkat I (II/d)" },
                    { value: "IIIA", label: "Penata Muda (III/a)" },
                    { value: "IIIB", label: "Penata Muda Tingkat I (III/b)" },
                    { value: "IIIC", label: "Penata (III/c)" },
                    { value: "IIID", label: "Penata Tingkat I (III/d)" },
                    { value: "IVA", label: "Pembina (IV/a)" },
                    { value: "IVB", label: "Pembina Tingkat I (IV/b)" },
                    { value: "IVC", label: "Pembina Utama Muda (IV/c)" },
                    { value: "IVD", label: "Pembina Utama Madya (IV/d)" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Pangkat/Golongan"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Status
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.status
                      ? {
                          value: formData.status,
                          label: formData.status === "user" ? "User" : "Admin",
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, status: newValue?.value || "" });
                  }}
                  options={[
                    { value: "user", label: "User" },
                    { value: "admin", label: "Admin" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Status"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="select"
                  label="Unit"
                  fullWidth
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  options={[
                    { value: "", label: "Pilih Unit" },
                    { value: "Sekretariat", label: "Sekretariat" },
                    { value: "Sub Bagian", label: "Sub Bagian" },
                    { value: "Bidang", label: "Bidang" },
                    { value: "Seksi", label: "Seksi" },
                    { value: "UPT", label: "UPT" },
                    { value: "Lainnya", label: "Lainnya" },
                  ]}
                />
              </Grid>
            </Grid>

            {/* Socials Section */}
            <MDBox mb={3} mt={4}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Social Media
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Akun media sosial user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="text"
                  label="No HP"
                  fullWidth
                  value={formData.noHP}
                  onChange={(e) =>
                    setFormData({ ...formData, noHP: e.target.value })
                  }
                  placeholder="Nomor HP/WhatsApp"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>phone</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Status EO
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.status_EO
                      ? {
                          value: formData.status_EO,
                          label: formData.status_EO,
                        }
                      : { value: "Aktif", label: "Aktif" }
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      status_EO: newValue?.value || "Aktif",
                    });
                  }}
                  options={[
                    { value: "Aktif", label: "Aktif" },
                    { value: "Non Aktif", label: "Non Aktif" },
                    {
                      value: "Pindah Ke Luar Kabupaten",
                      label: "Pindah Ke Luar Kabupaten",
                    },
                    { value: "Meninggal", label: "Meninggal" },
                    { value: "Pensiun", label: "Pensiun" },
                    { value: "Dipecat", label: "Dipecat" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Status EO"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="text"
                  label="Facebook Account"
                  fullWidth
                  value={formData.facebook}
                  onChange={(e) =>
                    setFormData({ ...formData, facebook: e.target.value })
                  }
                  placeholder="Username Facebook"
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ color: "#1877F2", mr: 1 }}>facebook</Icon>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="text"
                  label="Instagram Account"
                  fullWidth
                  value={formData.instagram}
                  onChange={(e) =>
                    setFormData({ ...formData, instagram: e.target.value })
                  }
                  placeholder="Username Instagram"
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ color: "#E1306C", mr: 1 }}>camera_alt</Icon>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Profile Section */}
            <MDBox mb={3} mt={4}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Profile
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Informasi tambahan dan bio user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  type="text"
                  label="Bio"
                  fullWidth
                  multiline
                  rows={5}
                  value={formData.bio}
                  onChange={(e) =>
                    setFormData({ ...formData, bio: e.target.value })
                  }
                  placeholder="Tuliskan bio atau deskripsi singkat tentang user"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <MDButton
              onClick={() => {
                // Cleanup preview URL saat dialog ditutup
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setOpenDialog(false);
                setSelectedFile(null);
                setPreviewUrl(null);
                setIsValidFile(false);
                setProfileImgError(false);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              variant="outlined"
              color="secondary"
            >
              Batal
            </MDButton>
            <MDButton onClick={handleEdit} variant="gradient" color="info">
              Simpan Perubahan
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default DaftarUsers;
